import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { RootLayout } from '@/components/RootLayout'
import { KeyEntryPage } from '@/routes/login'
import { DashboardPage } from '@/routes/dashboard'
import { FleetPage } from '@/routes/fleet'
import { InstanceDetailPage } from '@/routes/fleet.$instanceId'
import { AccountsPage } from '@/routes/accounts'
import { AccountDetailPage } from '@/routes/accounts.$accountId'
import { LicensesPage } from '@/routes/licenses'
import { UsagePage } from '@/routes/usage'
import { OperatorsPage } from '@/routes/operators'
import { CustomerRequestsPage } from '@/routes/customer-requests'
import { hasStoredSession } from '@/lib/auth/session'

// Code-based route tree (TanStack Router supports both file-based and
// programmatic route trees; this app uses the programmatic form
// deliberately -- see README note in this file's sibling doc comment in
// the final report: file-based codegen requires running the Vite dev
// server/build once to produce routeTree.gen.ts, which is fragile to set
// up correctly in a one-shot non-interactive build without a working
// browser round-trip to verify. The programmatic API is fully typed and
// avoids that generation step while using the exact same router/library
// as admin/chat/core).
const rootRoute = createRootRoute({
  component: () => (
    <RootLayout>
      <Outlet />
    </RootLayout>
  ),
})

// beforeLoad guard: any route under the authenticated shell redirects to
// /login when there is no stored session. This is a coarse, fast
// client-side gate (real enforcement is server-side per ADR-119); it only
// exists to avoid flashing an authenticated-looking empty page before the
// AuthProvider's own probe resolves.
function requireSession() {
  if (!hasStoredSession()) {
    throw redirect({ to: '/login' })
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: KeyEntryPage,
})

const fleetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: requireSession,
  component: FleetPage,
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: requireSession,
  component: DashboardPage,
})

const instanceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/$instanceId',
  beforeLoad: requireSession,
  component: InstanceDetailPage,
})

const accountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts',
  beforeLoad: requireSession,
  component: AccountsPage,
})

const accountDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accounts/$accountId',
  beforeLoad: requireSession,
  component: AccountDetailPage,
})

const licensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses',
  beforeLoad: requireSession,
  component: LicensesPage,
})

const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/usage',
  beforeLoad: requireSession,
  component: UsagePage,
})

const operatorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operators',
  beforeLoad: requireSession,
  component: OperatorsPage,
})

const customerRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customer-requests',
  beforeLoad: requireSession,
  component: CustomerRequestsPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  fleetRoute,
  dashboardRoute,
  instanceDetailRoute,
  accountsRoute,
  accountDetailRoute,
  licensesRoute,
  usageRoute,
  operatorsRoute,
  customerRequestsRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
